/**
 * Grouping the customer list into books of business, with rollups.
 *
 * The Customers table could always FILTER to an advisor but never GROUP by one,
 * so "what does Sarah's book look like, and how does it compare to Dave's" meant
 * running the filter once per person and writing the numbers down. This module is
 * the arithmetic behind doing it in one pass.
 *
 * It is pure and separate from the page because every interesting failure here is
 * a WRONG NUMBER rather than a crash, and a wrong total in a rollup is invisible:
 * it looks exactly like a right one. Three of those are encoded below.
 */

/** Which column defines a "book". */
export type GroupDimension = 'none' | 'ae' | 'csm' | 'ops';

/** The fields grouping reads. Structural so the page's fuller type satisfies it. */
export interface GroupableCustomer {
  customer_id: string;
  cust_ae: string | null;
  cust_csm: string | null;
  cust_ops_mgr: string | null;
  total_spend_12m: number | null;
  travelers_12m: number | null;
  health_score: number | null;
  health_band: string | null;
  /** Trailing-90-day PNR counts. Summed into `transactions`. */
  air_pnrs_90?: number | null;
  hotel_pnrs_90?: number | null;
  car_pnrs_90?: number | null;
  /** Last-12-month spend split, for the online share of the book. */
  online_spend_12m?: number | null;
  full_service_spend_12m?: number | null;
}

export interface Rollup {
  accounts: number;
  spend: number;
  travelers: number;
  /**
   * Mean health across customers that HAVE a score, or null when none do.
   *
   * Nulls are excluded rather than counted as zero. A customer with no score is
   * unmeasured, not unhealthy — averaging it in as 0 drags a healthy book down
   * and would make the column read as a problem that isn't there. `null` when the
   * whole group is unscored, so the UI can say "—" instead of "0".
   */
  avgHealth: number | null;
  /** Customers whose band marks them at risk. Counted, never inferred from the mean. */
  atRisk: number;
  /**
   * Transactions the book supported — distinct air, hotel and car PNRs over the
   * trailing 90 days.
   *
   * A FACT ABOUT THE BOOK, not a score. This deliberately does not rank anyone:
   * comparing CSMs against each other is the Domo customer-management app's job
   * (Taylor, 2026-08-21 — "the metrics to measure CSMs is for domo, I just want
   * the crm to be able to view CSM book of business"). Here it answers "how much
   * work does this book carry", which is the question you have while looking at
   * the accounts themselves.
   */
  transactions: number;
  /**
   * Online share of the book's 12-month spend, 0..1, or null when it has none.
   *
   * Share of SPEND, not of transactions — spend is what the customer row carries.
   * Null rather than 0 for a book with no spend at all, because 0% reads as
   * "nothing booked online" when the truth is "nothing booked".
   */
  onlineShare: number | null;
}

export interface CustomerGroup<T> {
  /** Stable key for React and for `aria-controls`. */
  key: string;
  /** What to show in the group header. */
  label: string;
  /** True for the catch-all group of customers with nobody in this role. */
  unassigned: boolean;
  customers: T[];
  rollup: Rollup;
}

/** The label used for the catch-all group. */
export const UNASSIGNED_LABEL = 'Unassigned';

/** Health bands that count toward `atRisk`. Matched case-insensitively. */
const AT_RISK_BANDS = new Set(['at risk', 'at-risk', 'critical', 'poor']);

function dimensionValue(
  c: GroupableCustomer,
  dimension: Exclude<GroupDimension, 'none'>,
): string | null {
  const raw =
    dimension === 'ae'
      ? c.cust_ae
      : dimension === 'csm'
        ? c.cust_csm
        : c.cust_ops_mgr;
  // Trim before deciding: Customer Master carries whitespace-only values in this
  // column, and an untrimmed '  ' would create its own phantom group whose header
  // renders blank — a book belonging to nobody, sitting between two real people.
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}

export function rollupOf(customers: readonly GroupableCustomer[]): Rollup {
  let spend = 0;
  let travelers = 0;
  let healthSum = 0;
  let healthCount = 0;
  let atRisk = 0;
  let transactions = 0;
  let online = 0;
  let fullService = 0;

  for (const c of customers) {
    spend += c.total_spend_12m ?? 0;
    travelers += c.travelers_12m ?? 0;
    transactions +=
      (c.air_pnrs_90 ?? 0) + (c.hotel_pnrs_90 ?? 0) + (c.car_pnrs_90 ?? 0);
    online += c.online_spend_12m ?? 0;
    fullService += c.full_service_spend_12m ?? 0;
    // Only real numbers participate in the mean — see the note on `avgHealth`.
    // `typeof` rather than a truthiness check, so a legitimate score of 0 counts.
    if (typeof c.health_score === 'number' && !Number.isNaN(c.health_score)) {
      healthSum += c.health_score;
      healthCount += 1;
    }
    const band = c.health_band?.trim().toLowerCase();
    if (band && AT_RISK_BANDS.has(band)) atRisk += 1;
  }

  return {
    accounts: customers.length,
    spend,
    travelers,
    avgHealth: healthCount > 0 ? healthSum / healthCount : null,
    atRisk,
    transactions,
    onlineShare:
      online + fullService > 0 ? online / (online + fullService) : null,
  };
}

/**
 * Group the (already filtered and sorted) customer rows into books.
 *
 * `dimension: 'none'` returns a single unlabelled group holding every row, so the
 * page renders a flat table through the same code path instead of branching — one
 * rendering path means the flat and grouped views cannot drift.
 *
 * ORDER. Groups are sorted by spend descending, because "who carries the most" is
 * the question the view exists to answer. `Unassigned` is PINNED LAST regardless
 * of its size — it is not a person's book, and on the live data it is one of the
 * largest "books" there is (~200 of ~780 active customers carry no AE and no CSM),
 * so sorting it by spend would park it at the top and bury every real advisor
 * beneath it.
 *
 * UNASSIGNED IS NEVER DROPPED. Excluding rows with no advisor would silently
 * shrink the totals — a quarter of the book vanishing from a view whose whole
 * purpose is to account for all of it. That is the single worst thing this
 * function could do, so it has its own test.
 */
export function groupCustomers<T extends GroupableCustomer>(
  customers: readonly T[],
  dimension: GroupDimension,
): CustomerGroup<T>[] {
  if (dimension === 'none') {
    return [
      {
        key: 'all',
        label: 'All customers',
        unassigned: false,
        customers: [...customers],
        rollup: rollupOf(customers),
      },
    ];
  }

  const byOwner = new Map<string, T[]>();
  const unassigned: T[] = [];

  for (const c of customers) {
    const owner = dimensionValue(c, dimension);
    if (owner === null) {
      unassigned.push(c);
      continue;
    }
    const bucket = byOwner.get(owner);
    if (bucket) bucket.push(c);
    else byOwner.set(owner, [c]);
  }

  const groups: CustomerGroup<T>[] = [...byOwner.entries()].map(
    ([owner, rows]) => ({
      // Namespaced `owner:` so a customer whose AE is literally named
      // "Unassigned" cannot collide with the catch-all group's key below — the
      // keys become React keys and aria-controls targets, where a collision wires
      // one group's disclosure button to another group's rows.
      key: `${dimension}:owner:${owner}`,
      label: owner,
      unassigned: false,
      customers: rows,
      rollup: rollupOf(rows),
    }),
  );

  groups.sort((a, b) => {
    if (b.rollup.spend !== a.rollup.spend)
      return b.rollup.spend - a.rollup.spend;
    // Ties broken by name so the order is STABLE across renders. Without this,
    // two advisors with no spend swap places depending on Map insertion order,
    // which reads as the table flickering for no reason.
    return a.label.localeCompare(b.label);
  });

  if (unassigned.length > 0) {
    groups.push({
      key: `${dimension}:unassigned`,
      label: UNASSIGNED_LABEL,
      unassigned: true,
      customers: unassigned,
      rollup: rollupOf(unassigned),
    });
  }

  return groups;
}

/** Human label for the grouping control and the group-header column. */
export const DIMENSION_LABEL: Record<GroupDimension, string> = {
  none: 'No grouping',
  ae: 'AE',
  csm: 'CSM',
  ops: 'Ops Manager',
};
